import { ClientQuestionnaireAnswersEntity } from '@/entities/client-questionnaire.entity';
import { ClientAiUsageSummaryEntity } from '@/entities/client-ai-usage-summary.entity';
import { ClientAnswerKindSummaryEntity } from '@/entities/client-answer-kind-summary.entity';
import { ClientBillingSummaryEntity } from '@/entities/client-billing.entity';
import { ClientFeatureControlsEntity } from '@/entities/client-feature-controls.entity';
import { ClientMarketplaceAvailabilityEntity } from '@/entities/client-marketplace-availability.entity';
import { ClientMarketplaceSettingsConstraintsEntity } from '@/entities/client-marketplace-settings-constraints.entity';
import { ClientMarketplaceSettingsEntity } from '@/entities/client-marketplace-settings.entity';
import { Marketplace } from '@/enums/marketplace';
import {
    buildEmptyClientAiProfileAnswers,
    createDefaultClientFeatureControls,
    createDefaultClientMarketplaceSettingsConstraintsEntity,
    createDefaultClientMarketplaceSettingsEntity,
    normalizeClientSettingsEntity,
} from '@/shared/client-settings';
import {
    ClientQuestionnaireAnswerId,
    CLIENT_QUESTIONNAIRE_VISIBLE_QUESTIONS,
    ClientQuestionnaireVisibleQuestionDefinition,
    countClientQuestionnaireVisibleAnswers,
} from '@/shared/client-questionnaire-definition';
import { RuntimeSupportedMarketplace } from '@/shared/marketplace';

export type QuestionnaireOption = {
    value: string;
    label: string;
};

export type QuestionnaireAnswerId = ClientQuestionnaireAnswerId;

export type QuestionnaireQuestion = {
    id: QuestionnaireAnswerId;
    index: number;
    title: string;
    options: QuestionnaireOption[];
};

export type QuestionnaireState = {
    version: string;
    storeName: string;
    companyInfoLink: string;
    email: string;
    answers: ClientQuestionnaireAnswersEntity;
    comments: string;
    aiUsage: ClientAiUsageSummaryEntity;
    answerKinds: ClientAnswerKindSummaryEntity;
    billing: ClientBillingSummaryEntity;
    settings: {
        general: ClientFeatureControlsEntity;
        marketplaces: ClientMarketplaceSettingsEntity;
        marketplaceSettingsConstraints: ClientMarketplaceSettingsConstraintsEntity;
    };
};

export type QuestionnaireGeneralSettingId = keyof ClientFeatureControlsEntity;
export type QuestionnaireChannelId = keyof ClientMarketplaceAvailabilityEntity;
export type QuestionnaireRuntimeMarketplaceId = RuntimeSupportedMarketplace;

export type SettingsTabId =
    | 'general'
    | 'channels'
    | 'review-import'
    | 'ai-strategy'
    | 'auto-replies'
    | 'answer-templates'
    | 'limits-payments'
    | 'statistics'
    | 'operations-history'
    | 'notifications';

export const SETTINGS_TABS: Array<{ id: SettingsTabId; label: string }> = [
    { id: 'general', label: 'Общие' },
    { id: 'channels', label: 'Каналы' },
    { id: 'ai-strategy', label: 'ИИ-стратегия' },
    { id: 'auto-replies', label: 'Автоответы' },
    { id: 'answer-templates', label: 'Шаблоны' },
    { id: 'statistics', label: 'Статистика' },
    { id: 'limits-payments', label: 'Лимиты, платежи' },
    { id: 'operations-history', label: 'История операций' },
    { id: 'notifications', label: 'Уведомления' },
];

export const EDITABLE_SETTINGS_TAB_IDS = [
    'general',
    'channels',
    'review-import',
    'ai-strategy',
] as const satisfies readonly SettingsTabId[];

export type EditableSettingsTabId = (typeof EDITABLE_SETTINGS_TAB_IDS)[number];

export const isEditableSettingsTab = (tabId: SettingsTabId): tabId is EditableSettingsTabId =>
    EDITABLE_SETTINGS_TAB_IDS.includes(tabId as EditableSettingsTabId);

export const QUESTIONNAIRE_GENERAL_SETTINGS_OPTIONS: Array<{
    id: QuestionnaireGeneralSettingId;
    label: string;
}> = [
    { id: 'allowResponseSending', label: 'Разрешить отправку ответов' },
    { id: 'allowAiAnswerGeneration', label: 'Разрешить формировать ИИ-ответы' },
    { id: 'allowAiAnalysis', label: 'Разрешить проводить ИИ-анализ отзывов' },
    { id: 'autoReviewAiAnalysisEnabled', label: 'Проводить ИИ-анализ входящих отзывов' },
    {
        id: 'autoReviewProcessingEnabled',
        label: 'Активировать автоматическую обработку и отправку ответов (Автоответы)',
    },
];

export const QUESTIONNAIRE_RUNTIME_MARKETPLACE_OPTIONS: Array<{
    id: QuestionnaireRuntimeMarketplaceId;
    label: string;
}> = [
    { id: Marketplace.Wb, label: 'Wildberries' },
    { id: Marketplace.Ozon, label: 'Ozon' },
];

export const QUESTIONNAIRE_CHANNEL_OPTIONS: Array<{
    id: QuestionnaireChannelId;
    label: string;
    disabled?: boolean;
    hint?: string;
}> = [
    ...QUESTIONNAIRE_RUNTIME_MARKETPLACE_OPTIONS,
    { id: 'yandexMarket', label: 'ЯндексМаркет', disabled: true, hint: 'скоро' },
];

const toQuestionnaireQuestion = (question: ClientQuestionnaireVisibleQuestionDefinition): QuestionnaireQuestion => ({
    id: question.id,
    index: question.index,
    title: question.title,
    options: question.options.map((option) => ({
        value: option,
        label: option,
    })),
});

export const QUESTIONNAIRE_QUESTIONS: QuestionnaireQuestion[] =
    CLIENT_QUESTIONNAIRE_VISIBLE_QUESTIONS.map(toQuestionnaireQuestion);

export const buildEmptyAnswers = (): ClientQuestionnaireAnswersEntity => buildEmptyClientAiProfileAnswers();

export const createDefaultQuestionnaireSettings = (): QuestionnaireState['settings'] => ({
    general: createDefaultClientFeatureControls(),
    marketplaces: createDefaultClientMarketplaceSettingsEntity(),
    marketplaceSettingsConstraints: createDefaultClientMarketplaceSettingsConstraintsEntity(),
});

export const createDefaultQuestionnaireState = (): QuestionnaireState => ({
    version: normalizeClientSettingsEntity(null).version,
    storeName: '',
    companyInfoLink: '',
    email: '',
    answers: buildEmptyAnswers(),
    comments: '',
    aiUsage: normalizeClientSettingsEntity(null).aiUsage,
    answerKinds: normalizeClientSettingsEntity(null).answerKinds,
    billing: normalizeClientSettingsEntity(null).billing,
    settings: createDefaultQuestionnaireSettings(),
});

export const normalizeQuestionnaireState = (data: unknown): QuestionnaireState => {
    const normalizedAnswers = buildEmptyAnswers();
    const normalizedSettings = normalizeClientSettingsEntity(data);
    const sourceAnswers = normalizedSettings.aiProfile.answers;

    for (const question of QUESTIONNAIRE_QUESTIONS) {
        const rawAnswer = sourceAnswers[question.id];
        if (!rawAnswer) {
            continue;
        }

        const isValidOption = question.options.some((option) => option.value === rawAnswer);
        if (isValidOption) {
            normalizedAnswers[question.id] = rawAnswer;
        }
    }

    return {
        version: normalizedSettings.version,
        storeName: normalizedSettings.aiProfile.storeName,
        companyInfoLink: normalizedSettings.aiProfile.companyInfoLink ?? '',
        email: normalizedSettings.clientProfile.email,
        answers: normalizedAnswers,
        comments: normalizedSettings.aiProfile.comments ?? '',
        aiUsage: normalizedSettings.aiUsage,
        answerKinds: normalizedSettings.answerKinds,
        billing: normalizedSettings.billing,
        settings: {
            general: normalizedSettings.featureControls,
            marketplaces: normalizedSettings.marketplaceSettings,
            marketplaceSettingsConstraints: normalizedSettings.marketplaceSettingsConstraints,
        },
    };
};

export const countAnsweredQuestionnaireQuestions = (answers: ClientQuestionnaireAnswersEntity): number =>
    countClientQuestionnaireVisibleAnswers(answers);
